/*
 * WORK > Requests (the old Steward's Requests list, 09-lists.js): everyone who asked for work, from the website form or typed
 * in, and what became of each. A request is a lead; its quote is made and sent on Quotes. The list has the overview counts,
 * the new ones in the past 30 days and how many turned into work; one request's page shows what they sent, its quote,
 * notes, the text back and their yes to texts.
 */
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  asksForPlan,
  HEARD_FROM,
  type HeardFrom,
  QUOTE_STATUS_WORDS,
  REQUEST_STATE_WORDS,
  REQUEST_STATES,
  requestSettled,
  requestStats,
  type RequestDetail,
  type RequestRow,
  type QuoteStatus,
  type RequestState,
} from "@steward/shared";
import { useCreateLead, useHeardFrom, useRequest, useRequestAction, useRequests } from "../api/money";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { FormField, InputText } from "../components/fields";
import { dayDate, momentIn, money, moneyExact, phoneText, shortDate } from "../components/format";
import { Menu, Modal, useToast } from "../components/overlays";
import { PlanOfferSend } from "../components/PlanOffer";
import { Icon } from "../components/Icon";
import { AddressLines, DirectionsButton } from "../components/Directions";
import { PhoneRecord } from "../components/PhoneRecord";
import { usePhone } from "../components/usePhone";
import {
  ago,
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  IconButton,
  LoadError,
  Page,
  Skeleton,
  StatusLabel,
  type Status,
} from "../components/parts";
import { errText, LINE_OFTEN, NotesCard } from "../components/work";
import { CallsCard, WalkthroughCard } from "./RequestCalls";
import { SettingsButton } from "./settings/Feature";
import { Thumb } from "./SocialPosts";

const DAY = 86_400_000;
const LOOK: Record<RequestState, Status> = { new: "informative", sent: "warning", converted: "success", lost: "inactive" };
const FIRST: Record<RequestState, number> = { new: 0, sent: 1, converted: 2, lost: 3 };

/** A new request waiting over a day turns warning, over three days critical, and says how long in words. */
export function RequestTag({ r, now = Date.now() }: { r: Pick<RequestRow, "state" | "archived" | "createdAt">; now?: number }) {
  // Converted and lost ones are archived on their own and keep their own tag.
  if (r.archived && !requestSettled(r.state)) return <StatusLabel status="inactive">Archived</StatusLabel>;
  const waited = now - Date.parse(r.createdAt);
  if (r.state === "new" && waited > DAY)
    return (
      <StatusLabel status={waited > 3 * DAY ? "critical" : "warning"}>
        New, waiting {ago(r.createdAt, now).replace(/ ago$/, "")}
      </StatusLabel>
    );
  return <StatusLabel status={LOOK[r.state]}>{REQUEST_STATE_WORDS[r.state]}</StatusLabel>;
}

/** Where they found us, in words. */
const sourceWords = (s: string | null) =>
  s === "my_trades" ? "My Trades" : s ? s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : null;

type Pick_ = RequestState | "all" | "archived";
const RANGES = [
  ["all", "Any time"],
  ["7", "Past 7 days"],
  ["30", "Past 30 days"],
  ["90", "Past 90 days"],
] as const;
type Range = (typeof RANGES)[number][0];

export function RequestsPage() {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const list = useRequests(company.id);
  const act = useRequestAction();
  const [status, setStatus] = useState<Pick_>("all");
  const [range, setRange] = useState<Range>("all");
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  // Create, Request in the side menu lands here with ?new=true: open the box, then drop it from the address.
  const search = useSearch({ strict: false });
  useEffect(() => {
    if (!search.new) return;
    setAdding(true);
    void navigate({ to: "/requests", search: {}, replace: true });
  }, [search.new, navigate]);
  const all = list.data?.requests ?? [];
  const stats = requestStats(all);
  const editable = can("lead.update");
  const since = range === "all" ? 0 : Date.now() - Number(range) * DAY;
  const rows = all
    .filter(
      (r) =>
        // Converted and lost requests archive on their own, so their picks show them from the archive too.
        (status === "archived"
          ? r.archived
          : status === "converted" || status === "lost"
            ? r.state === status
            : !r.archived && (status === "all" || r.state === status)) && Date.parse(r.createdAt) >= since,
    )
    // Ones that need you first (New, then Quote sent), newest first in each.
    .sort((a, b) => FIRST[a.state] - FIRST[b.state]);
  const archiveTicked = () => {
    const ids = [...ticked];
    void Promise.all(ids.map((id) => act.mutateAsync({ id, act: "archive" })))
      .then(() => {
        setTicked(new Set());
        toast(`${String(ids.length)} archived`, {
          undo: () => void Promise.all(ids.map((id) => act.mutateAsync({ id, act: "restore" }))),
        });
      })
      .catch((e: unknown) => toast(errText(e), { kind: "error" }));
  };
  return (
    <Page
      title="Requests"
      secondaryAction={<SettingsButton to="/requests/settings">Request settings</SettingsButton>}
      primaryAction={
        editable ? (
          <Button icon="plus" onClick={() => setAdding(true)}>
            New request
          </Button>
        ) : null
      }
    >
      <div className="tiles">
        <div className="tile">
          <span className="label">Overview</span>
          <div className="chips">
            {REQUEST_STATES.map((s) => (
              <Chip key={s} pressed={status === s} onClick={() => setStatus(status === s ? "all" : s)}>
                {REQUEST_STATE_WORDS[s]} ({list.data ? stats.counts[s] : "-"})
              </Chip>
            ))}
          </div>
        </div>
        <div className="tile">
          <span className="label">New requests</span>
          <span className="value">{list.data ? stats.recent : <span className="skeleton inline" />}</span>
          <span className="hint">Past 30 days</span>
        </div>
        <div className="tile">
          <span className="label">Conversion rate</span>
          <span className="value">{stats.recentRate === null ? "-" : `${String(stats.recentRate)}%`}</span>
          <span className="hint">Past 30 days, requests that became approved work</span>
        </div>
        <div className="tile">
          <span className="label">All time</span>
          <span className="value">{stats.allRate === null ? "-" : `${String(stats.allRate)}%`}</span>
          <span className="hint">
            {stats.converted} of {stats.all} converted
          </span>
        </div>
      </div>
      <DataTable<RequestRow>
        title="All requests"
        rows={rows}
        rowKey={(r) => r.id}
        loading={list.isFetching}
        error={list.error ? <LoadError error={list.error} retry={() => void list.refetch()} /> : null}
        onRowClick={(r) => void navigate({ to: "/requests/$id", params: { id: r.id } })}
        search={(r) => [r.name, r.service, r.address, r.phone, r.email, r.source, r.quote?.number].join(" ")}
        {...(editable && status !== "archived" && status !== "converted" && status !== "lost"
          ? { selected: ticked, onSelect: setTicked }
          : {})}
        bulk={
          <Button type="secondary" size="small" loading={act.isPending} onClick={archiveTicked}>
            Archive
          </Button>
        }
        tools={
          <>
            <div className="chips">
              <Chip pressed={status === "all"} onClick={() => setStatus("all")}>
                All
              </Chip>
              {REQUEST_STATES.map((s) => (
                <Chip key={s} pressed={status === s} onClick={() => setStatus(s)}>
                  {REQUEST_STATE_WORDS[s]}
                </Chip>
              ))}
              <Chip pressed={status === "archived"} onClick={() => setStatus("archived")}>
                Archived ({stats.archived})
              </Chip>
            </div>
            <div className="chips">
              {RANGES.map(([k, label]) => (
                <Chip key={k} pressed={range === k} onClick={() => setRange(k)}>
                  {label}
                </Chip>
              ))}
            </div>
          </>
        }
        empty={
          all.length ? (
            <EmptyState icon="requests" title="No requests match">
              Try another status or time.
            </EmptyState>
          ) : (
            <EmptyState icon="requests" title="No requests yet">
              When someone sends the quote form on the website, the request shows up here and on the pipeline.
            </EmptyState>
          )
        }
        columns={[
          {
            key: "client",
            header: "Client",
            sort: (r) => r.name.toLowerCase(),
            render: (r) => (
              <div className="name-cell">
                <b>{r.name}</b>
                <span className="muted">
                  {r.service ?? "Quote request"}
                  {r.quote ? ` · ${money(r.quote.totalCents)}` : r.valueCents ? ` · ${money(r.valueCents)}` : ""}
                </span>
              </div>
            ),
          },
          { key: "property", header: "Property", render: (r) => r.address ?? <span className="muted">Not given</span> },
          {
            key: "contact",
            header: "Contact",
            render: (r) => (
              <div className="name-cell">
                <span>{phoneText(r.phone) ?? ""}</span>
                <span className="muted">{r.email ?? ""}</span>
              </div>
            ),
          },
          {
            key: "from",
            header: "Found us",
            sort: (r) => r.source ?? "",
            render: (r) => sourceWords(r.source) ?? <span className="muted">Not known</span>,
          },
          {
            key: "requested",
            header: "Requested",
            sort: (r) => r.createdAt,
            render: (r) => shortDate(r.createdAt.slice(0, 10)),
          },
          {
            key: "status",
            header: "Status",
            sort: (r) => REQUEST_STATES.indexOf(r.state),
            render: (r) => <RequestTag r={r} />,
          },
        ]}
      />
      <NewRequest
        open={adding}
        onClose={() => setAdding(false)}
        onMade={(id) => void navigate({ to: "/requests/$id", params: { id } })}
      />
    </Page>
  );
}

export function RequestPage({ id }: { id: string }) {
  const { can, company } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const req = useRequest(id);
  const act = useRequestAction();
  const phone = usePhone();
  const r = req.data;
  const [offering, setOffering] = useState(false);
  if (req.error)
    return (
      <Page title="Request">
        <LoadError error={req.error} retry={() => void req.refetch()} />
      </Page>
    );
  if (!r)
    return (
      <Page title={<span className="skeleton inline wide" />}>
        <Skeleton rows={6} />
      </Page>
    );
  const editable = can("lead.update");
  const run = (what: "archive" | "restore" | "decline", done: string, undo?: "archive" | "restore") =>
    act.mutate(
      { id: r.id, act: what },
      {
        onSuccess: () => toast(done, undo ? { undo: () => act.mutate({ id: r.id, act: undo }) } : {}),
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  // A draft quote opens in its editor (it has not gone out yet); a sent or signed one opens to read.
  const draftQuote = r.quote?.status === "draft" && can("quote.update");
  const openQuote = () => {
    if (!r.quote) return;
    const params = { id: r.quote.id };
    void (draftQuote ? navigate({ to: "/quotes/$id/edit", params }) : navigate({ to: "/quotes/$id", params }));
  };
  const quoteButton = r.quote ? (
    <Button icon={draftQuote ? "edit" : "quotes"} onClick={openQuote}>
      {draftQuote ? "Edit quote" : "Open quote"} #{r.quote.number}
    </Button>
  ) : editable && can("quote.update") && !r.archived && r.state !== "lost" ? (
    <Button icon="quotes" onClick={() => void navigate({ to: "/quotes/new", search: { leadId: r.id } })}>
      Make a quote
    </Button>
  ) : null;
  const primary =
    r.archived && editable && !requestSettled(r.state) ? (
      <Button onClick={() => run("restore", "Request put back")}>Restore</Button>
    ) : (
      quoteButton
    );
  const moreMenu =
    editable && !r.archived ? (
      <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
        {(close) => (
          <>
            {r.state === "new" || r.state === "sent" ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  run("decline", "Marked lost");
                }}
              >
                Decline the work
              </button>
            ) : null}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                run("archive", "Request archived", "restore");
              }}
            >
              Archive
            </button>
          </>
        )}
      </Menu>
    ) : null;
  // They asked for a regular cleaning and we have not sent a quote yet: send a plan offer from here (owner, 2026-11-02).
  const wantsPlan =
    company.trade === "cleaning" &&
    can("quote.update") &&
    Boolean(r.clientId) &&
    !r.quote &&
    !r.archived &&
    asksForPlan([r.service, r.note].filter(Boolean).join(" "));
  const banners = wantsPlan ? (
    <Banner type="notice">
      <b>They asked about a regular cleaning.</b> Send them a plan offer with their prices.{" "}
      <Button size="small" onClick={() => setOffering(true)}>
        Send offer
      </Button>
    </Banner>
  ) : r.archived && r.state === "converted" ? (
    <Banner type="notice">
      This became approved work, so it moved to Archived on its own. It stays on the pipeline and in your reports.
    </Banner>
  ) : r.archived && r.state === "lost" ? (
    <Banner type="notice">
      This was lost, so it moved to Archived on its own. It stays on the pipeline and in your reports. Move it out of Lost on the
      pipeline to bring it back.
    </Banner>
  ) : r.archived ? (
    <Banner type="notice">This is archived. It is off your lists and the pipeline. Restore puts it back the way it was.</Banner>
  ) : null;
  const offerSheet =
    offering && r.clientId ? (
      <PlanOfferSend clientId={r.clientId} opener="request" leadId={r.id} onClose={() => setOffering(false)} />
    ) : null;
  const got = momentIn(r.createdAt, company.timezone);
  // A My Trades request is the client's own words, so its title is where it came from and the words show in full below.
  const myTrades = r.source === "my_trades";
  const title = myTrades ? "Request from My Trades" : (r.service ?? "Quote request");
  // A repeating quote whose first visit costs more (a first cut of tall grass) shows both prices, not one total.
  const repeat = repeatWords(r.quote);
  const firstCents = repeat && r.quote ? r.quote.totalCents : r.valueCents;
  const overview = (
    <Card>
      <dl className="details grid req-facts">
        <div>
          <dt>About how much</dt>
          <dd>
            {firstCents ? moneyExact(firstCents) : <span className="muted">No price yet</span>}
            {repeat ? <span className="req-sub">First visit, then {repeat}</span> : null}
          </dd>
        </div>
        <div>
          <dt>Received</dt>
          <dd title={got.full}>
            {got.day}
            <span className="req-sub">
              {got.time} <span className="muted">· {ago(r.createdAt)}</span>
            </span>
          </dd>
        </div>
        <div>
          <dt>Found us through</dt>
          <dd>{sourceWords(r.source) ?? <span className="muted">Not known</span>}</dd>
        </div>
        <div>
          <dt>Heard about us</dt>
          <dd>
            <HeardFromPick id={r.id} value={r.heardFrom} />
          </dd>
        </div>
        <div>
          <dt>Pipeline stage</dt>
          <dd>{r.stage}</dd>
        </div>
      </dl>
    </Card>
  );
  const contact = (
    <Card title="Contact" flush>
      <div className="todo">
        <div className="item">
          <Icon name="person" />
          <div className="what">
            {r.clientId ? (
              <button
                type="button"
                className="link"
                onClick={() => void navigate({ to: "/clients/$id", params: { id: r.clientId ?? "" } })}
              >
                {r.name}
              </button>
            ) : (
              <b>{r.name}</b>
            )}
            <span>{r.clientId ? "Client" : "Not a client yet"}</span>
          </div>
        </div>
        {r.phone ? (
          <div className="item">
            <Icon name="phone" />
            <div className="what">
              <a href={`tel:${r.phone}`}>{phoneText(r.phone)}</a>
              <span>Phone</span>
            </div>
          </div>
        ) : null}
        {r.email ? (
          <div className="item">
            <Icon name="mail" />
            <div className="what">
              <a href={`mailto:${r.email}`}>{r.email}</a>
              <span>Email</span>
            </div>
          </div>
        ) : null}
        {r.address ? (
          <div className="item">
            <Icon name="pin" />
            <div className="what">
              <DirectionsButton address={r.address} className="link">
                <AddressLines address={r.address} />
              </DirectionsButton>
              <span>Directions</span>
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
  const whatSent = (
    <Card title="What they sent">
      <SentByThem r={r} />
    </Card>
  );
  // What they said on a phone call, and a walkthrough planned before the bid (RequestCalls.tsx).
  const callsCard = <CallsCard r={r} />;
  const walkCard = <WalkthroughCard r={r} />;
  const quoteCard = r.quote ? (
    <Card
      title="Quote"
      action={
        <Button type="tertiary" size="small" icon={draftQuote ? "edit" : "arrowRight"} onClick={openQuote}>
          {draftQuote ? "Edit" : "Open"}
        </Button>
      }
      flush
    >
      <div className="todo">
        <div className="item">
          <Icon name="quotes" />
          <div className="what">
            <b>Quote #{r.quote.number}</b>
            <span>
              {r.quote.status === "draft" ? "Not sent to them yet" : "Sent to them"}
              {repeat ? `. First visit, then ${repeat}` : null}
            </span>
          </div>
          <StatusLabel status={QUOTE_LOOK[r.quote.status]}>{QUOTE_STATUS_WORDS[r.quote.status]}</StatusLabel>
          <b className="req-total">{moneyExact(r.quote.totalCents)}</b>
        </div>
      </div>
    </Card>
  ) : null;
  const notesCard = <NotesCard on="requests" id={r.id} notes={r.notes} editable={editable && !r.archived} officeOnly />;
  const textBack = (
    <Card title="What we sent back" flush={r.replies.length > 0}>
      {r.replies.length ? (
        <div className="todo">
          {r.replies.map((m, i) => (
            <div className="item" key={i}>
              <Icon name={m.channel === "email" ? "mail" : "message"} />
              <div className="what">
                <b>{m.channel === "sms" ? "Text" : m.channel === "email" ? "Email" : m.channel}</b>
                <span>{ago(m.at)}</span>
              </div>
              <StatusLabel status={REPLY_LOOK[m.status] ?? "inactive"}>{REPLY_WORDS[m.status] ?? m.status}</StatusLabel>
            </div>
          ))}
        </div>
      ) : (
        <span className="muted">Nothing was sent back to them.</span>
      )}
    </Card>
  );
  const consent = r.consent.length ? (
    <Card title="Consent">
      {r.consent.map((c) => (
        <p key={c.at + c.kind} className="small">
          <b>{c.kind === "opt_in" ? "Said yes to offers by text" : c.kind === "stop" ? "Said stop" : "Said start"}</b>{" "}
          <span className="muted">
            {shortDate(c.at.slice(0, 10))}. The words they saw were {c.words}
          </span>
        </p>
      ))}
    </Card>
  ) : null;

  if (phone)
    return (
      <PhoneRecord
        back={{ label: "Back", go: () => void navigate({ to: "/requests" }) }}
        status={<RequestTag r={r} />}
        title={title}
        sub={
          r.clientId ? (
            <button
              type="button"
              className="link"
              onClick={() => void navigate({ to: "/clients/$id", params: { id: r.clientId ?? "" } })}
            >
              {r.name} <Icon name="chevronRight" size={16} />
            </button>
          ) : (
            r.name
          )
        }
        phone={r.phone}
        email={r.email}
        facts={[
          {
            key: "when",
            icon: "clock" as const,
            main: `Received ${got.day} at ${got.time}`,
            sub: [ago(r.createdAt), r.valueCents ? `About ${moneyExact(r.valueCents)}` : null, sourceWords(r.source)]
              .filter(Boolean)
              .join(" · "),
          },
          ...(r.address
            ? [{ key: "where", icon: "pin" as const, main: r.address, sub: "Directions", directions: r.address }]
            : []),
        ]}
        actions={
          <>
            {primary}
            {moreMenu}
          </>
        }
      >
        {banners}
        {offerSheet}
        {whatSent}
        {callsCard}
        {walkCard}
        {quoteCard}
        {textBack}
        {notesCard}
        {overview}
        {consent}
      </PhoneRecord>
    );

  return (
    <Page
      title={title}
      titleMetaData={<RequestTag r={r} />}
      subtitle={
        r.clientId ? (
          <button
            type="button"
            className="link"
            onClick={() => void navigate({ to: "/clients/$id", params: { id: r.clientId ?? "" } })}
          >
            {r.name}
          </button>
        ) : (
          r.name
        )
      }
      primaryAction={primary}
      moreActions={moreMenu}
    >
      {banners}
      {offerSheet}
      <div className="home-grid">
        <div className="stack">
          {overview}
          {whatSent}
          {callsCard}
          {quoteCard}
        </div>
        <div className="stack">
          {contact}
          {walkCard}
          {notesCard}
          {textBack}
          {consent}
        </div>
      </div>
    </Page>
  );
}

/** "$50.00 every 2 weeks" when a repeating quote's first visit costs more than each visit after (a first cut). */
export function repeatWords(q: RequestDetail["quote"]): string | null {
  return q?.repeatCents ? `${moneyExact(q.repeatCents)} ${(LINE_OFTEN[q.frequency] ?? "each visit").toLowerCase()}` : null;
}

/** What the form or the client sent, read as answers: on the request page and on its quote. */
export function SentByThem({ r }: { r: RequestDetail }) {
  const { company } = useSession();
  const got = momentIn(r.createdAt, company.timezone);
  const myTrades = r.source === "my_trades";
  const repeat = repeatWords(r.quote);
  const sent = r.note ? sentAnswers(r.note, Boolean(r.source)) : null;
  // An older website price line named one total for a repeating plan; the price on top says it right, so it is left out.
  if (sent && repeat) sent.pairs = sent.pairs.filter(([q, a]) => q !== "Website price" || a.includes(" then "));
  // A short My Trades message is the title on the server and not in the note, so it shows from there.
  const words = sent?.message.length ? sent.message : myTrades && r.service ? [r.service] : [];
  return (
    <>
      {sent || words.length ? (
        <div className="req-sent">
          <p className="req-got" title={got.full}>
            <Icon name="clock" size={16} />
            <span>
              Received {got.day} at {got.time}
              {sent?.from ? <span className="muted"> · {sent.from}</span> : null}
            </span>
          </p>
          {words.length ? (
            <blockquote className="req-message">
              {words.map((w, i) => (
                <p key={i}>{w}</p>
              ))}
            </blockquote>
          ) : null}
          {sent?.lines.length ? (
            <ul className="req-lines">
              {sent.lines.map((l, i) => (
                <li key={i}>{l}</li>
              ))}
            </ul>
          ) : null}
          {sent?.pairs.length ? (
            <dl className="details grid req-answers">
              {sent.pairs.map(([q, a], i) => (
                <div key={i} className={a.length > 60 ? "wide" : undefined}>
                  <dt>{q}</dt>
                  <dd>{a}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      ) : (
        <span className="muted">The form sent no answers.</span>
      )}
      {r.photos.length ? (
        <div className="req-photos" aria-label="Photos they sent">
          {r.photos.map((p, i) => (
            <Thumb key={p.id} fileId={p.id} alt={`Photo ${String(i + 1)}`} className="req-photo" />
          ))}
        </div>
      ) : null}
    </>
  );
}

const QUOTE_LOOK: Record<QuoteStatus, Status> = {
  draft: "inactive",
  sent: "warning",
  approved: "success",
  converted: "success",
  declined: "critical",
  expired: "inactive",
};

const REPLY_LOOK: Record<string, Status> = {
  sent: "success",
  queued: "informative",
  held: "warning",
  failed: "critical",
  cancelled: "inactive",
  dry_run: "inactive",
};

/**
 * The form's answers, one per line ("How big is the yard?: Medium"), split into question and answer so they read as a
 * list. The lines the server writes ("Wants 2026-11-16, morning", "New address ...") become answers too, "Sent from My
 * Trades ..." becomes `from`, and long plain lines are their own words (`message`). Short plain lines (the form name,
 * "2,700 sq ft of grass") stay tags. "Found us through" shows on top, so it is left out when the source is known.
 */
export function sentAnswers(
  note: string,
  hasSource: boolean,
): { from: string | null; message: string[]; lines: string[]; pairs: [string, string][] } {
  let from: string | null = null;
  const message: string[] = [];
  const lines: string[] = [];
  const pairs: [string, string][] = [];
  for (const raw of note.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const sentFrom = /^Sent from (.+?)\.?$/.exec(line);
    const wants = /^Wants (\d{4}-\d{2}-\d{2})(?:, (.+))?$/.exec(line);
    const said = /^(Time of day|New address|Website price) (.+)$/.exec(line);
    if (sentFrom?.[1]) {
      from = `From ${sentFrom[1]}`;
      continue;
    }
    if (wants?.[1]) {
      pairs.push(["Day they want", `${dayDate(wants[1])}${wants[2] ? `, ${wants[2]}` : ""}`]);
      continue;
    }
    if (said?.[1] && said[2]) {
      pairs.push([said[1], said[2].replace(/^\w/, (c) => c.toUpperCase())]);
      continue;
    }
    const at = line.indexOf(": ");
    const q =
      at > 0 && at <= 80
        ? line
            .slice(0, at)
            .replace(/\s*\(optional\)/i, "")
            .trim()
        : "";
    const a = q ? line.slice(at + 2).trim() : "";
    if (!q || !a) (line.length > 60 ? message : lines).push(line);
    else if (!(hasSource && /^found us( through)?$/i.test(q))) pairs.push([q, a]);
  }
  // The form's short summary ("771 sq ft of grass") repeats answers that show in full below, so those tags are left out.
  const said = pairs.map(([, a]) => a.toLowerCase());
  const tags = lines.filter((l) => !said.some((a) => a.startsWith(l.toLowerCase())));
  return { from, message, lines: tags, pairs };
}

const REPLY_WORDS: Record<string, string> = {
  sent: "Sent",
  queued: "Waiting to go",
  held: "Held",
  failed: "Did not go out",
  cancelled: "Stopped",
  dry_run: "Test mode, not sent",
};

/** "How did you hear about us?" Asked on the call or read from the form; the office picks or fixes it here. */
function HeardFromPick({ id, value }: { id: string; value: HeardFrom | null }) {
  const set = useHeardFrom();
  const toast = useToast();
  return (
    <select
      className="select"
      aria-label="How they heard about us"
      value={value ?? ""}
      disabled={set.isPending}
      onChange={(e) =>
        set.mutate(
          { id, heardFrom: (e.target.value || null) as HeardFrom | null },
          { onError: (err) => toast(errText(err), { kind: "error" }) },
        )
      }
    >
      <option value="">Not asked yet</option>
      {HEARD_FROM.map((h) => (
        <option key={h} value={h}>
          {h}
        </option>
      ))}
    </select>
  );
}

function NewRequest({ open, onClose, onMade }: { open: boolean; onClose: () => void; onMade: (id: string) => void }) {
  const create = useCreateLead();
  const toast = useToast();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [service, setService] = useState("");
  const [note, setNote] = useState("");
  const [heard, setHeard] = useState<HeardFrom | "">("");
  const [err, setErr] = useState<string | null>(null);
  const go = () => {
    if (!name.trim()) {
      setErr("Type a name.");
      return;
    }
    create.mutate(
      {
        name: name.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        address: address.trim() || null,
        service: service.trim() || null,
        note: note.trim() || null,
        source: "Typed in",
        heardFrom: heard || null,
      },
      {
        onSuccess: (lead) => {
          toast(`Request for ${name.trim()} added`);
          setName("");
          setPhone("");
          setEmail("");
          setAddress("");
          setService("");
          setNote("");
          setHeard("");
          setErr(null);
          onClose();
          onMade(lead.id);
        },
        onError: (e) => setErr(errText(e)),
      },
    );
  };
  return (
    <Modal
      title="New request"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={create.isPending} onClick={go}>
            Add request
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <InputText label="Name" value={name} error={err} onChange={(e) => setName(e.target.value)} />
        <InputText label="Mobile phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <InputText label="Email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <InputText label="Address" value={address} onChange={(e) => setAddress(e.target.value)} />
        <InputText label="What they want" value={service} onChange={(e) => setService(e.target.value)} />
        <InputText label="What they said" value={note} onChange={(e) => setNote(e.target.value)} />
        <FormField label="How did they hear about us?">
          <select className="select" value={heard} onChange={(e) => setHeard(e.target.value as HeardFrom | "")}>
            <option value="">Not asked yet</option>
            {HEARD_FROM.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
        </FormField>
      </div>
      <p className="muted small">It goes on the list as New and on the pipeline. Make the quote from its page.</p>
    </Modal>
  );
}
